// SmartFolderList — 사이드바 S3 섹션 (U38)
// 5개 Smart Folder를 렌더하고 클릭 시 URL 쿼리 파라미터를 갱신한다.
//
// 동작:
// - 각 폴더 클릭 → useSearchParams로 해당 ?파라미터 추가 / 기존 smart folder 파라미터 초기화
// - 현재 활성 폴더 시각 강조 (sidebar__nav-link active 클래스 적용)
// - 카운트 표시:
//   - pinned → useItems({ pinned: 1 }) 로 정확한 카운트 fetch
//   - 나머지 → placeholder (백엔드 쿼리 지원 시 U47에서 강화)
//   - trash → 항상 0 (Phase 2.5 U50~U54에서 실데이터 연결)
//
// 의존: lucide-react (U34), react-router-dom useSearchParams, useItems 훅

import { useSearchParams } from 'react-router-dom';
import { useItems } from '../../hooks/useItems';
import { Tooltip } from '../../ui/Tooltip';
import {
  getActiveSmartFolder,
  SMART_FOLDERS,
  type SmartFolderId,
} from '../../constants/smartFolders';

// ─── Smart Folder 카운트 훅 ─────────────────────────────────

/**
 * 각 Smart Folder별 카운트를 반환한다.
 * pinned만 정확한 fetch, 나머지는 placeholder(null).
 * null이면 카운트 배지를 렌더하지 않는다 (추후 U47에서 강화).
 */
function useSmartFolderCounts(): Record<SmartFolderId, number | null> {
  // pinned 폴더만 실 카운트 fetch
  const { data: pinnedData } = useItems({ pinned: 1, limit: 1 });

  // TODO: recent / expiring / unfiled / trash 카운트는 서버 쿼리 지원(U47) 후 fetch 추가
  return {
    recent: null,
    expiring: null,
    pinned: pinnedData?.items?.length ?? null,
    unfiled: null,
    trash: 0, // Phase 2.5 전까지 항상 0 고정
  };
}

// ─── SmartFolderList ────────────────────────────────────────

/**
 * Smart Folders 5개를 사이드바 섹션으로 렌더한다.
 * Pinned/Tree 섹션 다음, 설정 섹션 이전에 위치한다.
 */
export function SmartFolderList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = getActiveSmartFolder(searchParams);
  const counts = useSmartFolderCounts();

  /**
   * 폴더 클릭 핸들러.
   * 동일 폴더 재클릭 시 → 필터 해제 (전체 보기로 복귀).
   * 다른 폴더 클릭 시 → 기존 smart folder 파라미터를 모두 초기화하고 새 파라미터 설정.
   */
  function handleFolderClick(id: SmartFolderId) {
    const folder = SMART_FOLDERS.find((f) => f.id === id);
    if (!folder) return;

    const next = new URLSearchParams(searchParams);

    // 기존 smart folder 파라미터 초기화
    for (const f of SMART_FOLDERS) {
      next.delete(f.queryParam);
    }

    // 동일 폴더 재클릭이면 필터 해제, 다르면 새 파라미터 설정
    if (activeId !== id) {
      next.set(folder.queryParam, folder.queryValue);
    }

    setSearchParams(next, { replace: true });
  }

  return (
    <section className="sidebar__section" aria-label="스마트 폴더">
      <div className="sidebar__heading">
        <span>스마트 폴더</span>
      </div>
      <ul className="sidebar__list" role="list">
        {SMART_FOLDERS.map((folder) => {
          const Icon = folder.icon;
          const isActive = activeId === folder.id;
          const count = counts[folder.id];

          return (
            <li key={folder.id}>
              <Tooltip content={folder.tooltip} side="right" sideOffset={8} delayDuration={600}>
                <button
                  type="button"
                  className={`sidebar__nav-link sidebar__smart-folder-btn${isActive ? ' active' : ''}`}
                  onClick={() => handleFolderClick(folder.id)}
                  aria-pressed={isActive}
                  aria-label={folder.label}
                  data-folder-id={folder.id}
                >
                  <Icon size={14} aria-hidden />
                  <span className="sidebar__smart-folder-label">{folder.label}</span>
                  {/* 카운트 배지 — null이면 미표시, 0 이상이면 표시 */}
                  {count !== null && (
                    <span
                      className="sidebar__smart-folder-count"
                      aria-label={`${count}개`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              </Tooltip>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
